import React from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { FileTextOutlined, CalendarOutlined, RightOutlined } from "@ant-design/icons";
import type { PrescriptionOrder } from "../../types/prescription";
import { getStatusConfig } from "../../types/prescription";

type Props = {
  order: PrescriptionOrder;
  onViewDetails?: (order: PrescriptionOrder) => void;
};

export default function PrescriptionOrderCard({ order, onViewDetails }: Props) {
  const navigate = useNavigate();
  const statusConfig = getStatusConfig(order.status);
  const orderLabel = `Order ${order.orderId}`;
  const dateFormatted = order.schedule_date
    ? moment(order.schedule_date).format("MMM D, YYYY")
    : "—";

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(order);
    } else {
      navigate("/main/prescriptions/ordering");
    }
  };

  return (
    <article
      className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-[#06509E] focus-within:ring-offset-2"
      aria-labelledby={`order-heading-${order.id}`}
    >
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Thumbnail / icon */}
        <div
          className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center overflow-hidden"
          aria-hidden
        >
          {order.image ? (
            <img
              src={order.image}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <FileTextOutlined className="text-2xl sm:text-3xl text-neutral-400" aria-hidden />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h2
            id={`order-heading-${order.id}`}
            className="text-base sm:text-lg font-semibold text-neutral-900 truncate"
          >
            {orderLabel}
          </h2>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutral-600">
            <span className="flex items-center gap-1">
              <CalendarOutlined className="text-neutral-400" aria-hidden />
              {dateFormatted}
            </span>
          </div>
          <div className="mt-2">
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${statusConfig.bgClass} ${statusConfig.colorClass}`}
              aria-label={`Status: ${statusConfig.label}`}
            >
              {statusConfig.label}
            </span>
          </div>
        </div>

        {/* Action */}
        <div className="flex-shrink-0">
          <button
            type="button"
            onClick={handleViewDetails}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#06509E] hover:text-[#053d7a] focus:outline-none focus:underline"
            aria-label={`View details for ${orderLabel}`}
          >
            View details
            <RightOutlined className="text-xs" aria-hidden />
          </button>
        </div>
      </div>
    </article>
  );
}
